import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

import { serializeFormDates } from './dateHelpers';

/**
 * A FormGroup that can recursively transform its values and those of its children.
 * Supports nested TransformingFormGroups, regular FormGroups, FormArrays, and FormControls.
 *
 * @template T The type of the form data structure
 */
export class TransformingFormGroup<T = Record<string, unknown>> extends FormGroup {
  constructor(
    controls: { [K in keyof T]: AbstractControl },
    private transformFn?: (value: T) => T
  ) {
    super(controls);
  }

  /**
   * Recursively get transformed values from this group.
   * Regular FormGroups/FormControls will return their normal values.
   * TransformingFormGroups will return their values transformed by transformFn.
   * Dates are serialized as local YYYY-MM-DD so calendar dates are not shifted by UTC.
   */
  getTransformedValue(): T {
    const result: Record<string, unknown> = {};

    Object.keys(this.controls).forEach(key => {
      const control = this.controls[key];
      const value = (control as TransformingFormGroup).getTransformedValue
        ? (control as TransformingFormGroup).getTransformedValue()
        : control.value;

      result[key] = value;
    });

    const serialized = serializeFormDates(result);

    // Apply this group's transform function if provided
    return this.transformFn ? this.transformFn(serialized as T) : (serialized as T);
  }
}

/**
 * A FormArray that can recursively transform its values and those of its children.
 * Supports nested TransformingFormArrays, TransformingFormGroups, regular FormGroups, FormArrays, and FormControls.
 *
 * @template T The type of the form data structure
 */
export class TransformingFormArray<T = unknown> extends FormArray {
  constructor(
    controls: AbstractControl[],
    private transformFn?: (value: T[]) => T[]
  ) {
    super(controls);
  }

  /**
   * Recursively get transformed values from this array.
   * Regular FormGroups/FormControls will return their normal values.
   * TransformingFormArrays and TransformingFormGroups will return their values transformed by transformFn.
   * Dates are serialized as local YYYY-MM-DD so calendar dates are not shifted by UTC.
   */
  getTransformedValue(): T[] {
    const result: unknown[] = [];

    this.controls.forEach(control => {
      const value = (control as TransformingFormGroup).getTransformedValue
        ? (control as TransformingFormGroup).getTransformedValue()
        : control.value;

      result.push(value);
    });

    const serialized = serializeFormDates(result);

    // Apply this array's transform function if provided
    return this.transformFn ? this.transformFn(serialized as T[]) : (serialized as T[]);
  }
}
